import { isDefined } from '../utils';
import { hexToOklch } from './oklch';
import {
  SURFACE_TILE_IDS,
  THEME_SURFACE_TILE_KEY,
  type SurfaceTileId,
} from './surfaceTiles';
import {
  contrastRatio,
  mergeOrgTheme,
  parseSourceColorChannels,
  relativeLuminance,
  resolveThemeDefaults,
  surfaceMode,
} from './themeResolution';

/**
 * The three-state brand check (rebrand v2, spec §5): can the brand colour be
 * told apart from a background?
 *
 * One pure module the editor reads to grade and to warn. Nothing refuses a
 * save on it (owner decision, MV2-599): an excluded look is shown as a
 * warning, with the nearest passing option and the direction text to help,
 * and the owner decides. The grade, the per-option grades, the suggestion and
 * the direction text all live here so the editor never re-derives them.
 *
 * Every colour input accepts the engine's stored formats (`#RGB`, `#RRGGBB`,
 * `rgb()`, `rgba()`), parsed by `parseSourceColorChannels` before anything
 * reaches the hex-only OKLCH conversion.
 */

const PRIMARY_MAIN = '--mfw-palette-primary-main';
const BACKGROUND_DEFAULT = '--mfw-palette-background-default';
const BACKGROUND_PAPER = '--mfw-palette-background-paper';

/**
 * Where the states are cut (decision 6), compared against the UNROUNDED
 * ratio: a pair the badge renders as "3.0" may still sit below 3.
 */
export const BRAND_GRADE_THRESHOLDS = {
  recommended: 3,
  excluded: 1.5,
} as const;

export type BrandGradeState = 'recommended' | 'not-recommended' | 'excluded';

export type BrandGrade = {
  /** min(contrast vs page, contrast vs card), unrounded. */
  ratio: number;
  state: BrandGradeState;
  /** The page colour graded against, as resolved (any engine format). */
  defaultColor: string;
  /** The card colour graded against, as resolved (any engine format). */
  paperColor: string;
};

/**
 * Every option the Background section can offer: the ten catalog tiles, and
 * — when the tenant pinned a ladder — "Your platform's background", which is
 * the absence of an org `surface` (spec §3.2); plus `'custom'`, the org's own
 * colour, which is only ever shown and graded, never suggested.
 */
export type BackgroundOptionId = SurfaceTileId | 'platform' | 'custom';

/** The options a suggestion may name: everything but the owner's own colour. */
type SuggestableOptionId = Exclude<BackgroundOptionId, 'custom'>;

/**
 * Catalog order, the platform option last (it is the eleventh). `'custom'` is
 * not in it: `nearestPassingTile` suggests from this list, and a colour the
 * owner typed is never a suggestion.
 */
const BACKGROUND_OPTION_ORDER: readonly SuggestableOptionId[] = [
  ...SURFACE_TILE_IDS,
  'platform',
];

export function brandGradeState(ratio: number) {
  if (ratio >= BRAND_GRADE_THRESHOLDS.recommended) {
    return 'recommended';
  }
  if (ratio >= BRAND_GRADE_THRESHOLDS.excluded) {
    return 'not-recommended';
  }
  return 'excluded';
}

/**
 * Grades one brand colour on one background: the worse of its contrast with
 * the page (`default`) and with the cards on it (`paper`).
 *
 * Throws on a colour it cannot parse — same contract as `contrastRatio`. A
 * caller grades the EFFECTIVE merged colours, never malformed raw input.
 */
export function gradeBrandOnBackground(
  primaryColor: string,
  defaultColor: string,
  paperColor: string,
) {
  const ratio = Math.min(
    contrastRatio(primaryColor, defaultColor),
    contrastRatio(primaryColor, paperColor),
  );
  return {
    ratio,
    state: brandGradeState(ratio),
    defaultColor,
    paperColor,
  } satisfies BrandGrade;
}

/** Page and card colour the single theme merge resolves for these layers. */
function resolvedBackground(
  tenantTheme: Record<string, string> | null,
  orgTheme: Record<string, string>,
) {
  const resolved = mergeOrgTheme(resolveThemeDefaults(), tenantTheme, orgTheme);
  return {
    defaultColor: resolved[BACKGROUND_DEFAULT],
    paperColor: resolved[BACKGROUND_PAPER],
  };
}

/**
 * Grades one brand colour on every background option, each resolved by
 * `mergeOrgTheme` exactly as it would render:
 *
 * - a fixed tile on its own ladder;
 * - `brand-dark` on the ladder toned from THIS brand — not today's default
 *   green, and not a tenant pin (an explicit `brand-dark` beats the pin);
 * - the platform option, only when `tenantPin` is passed, on what the org
 *   resolves to with no `surface` under that tenant — the pin's own page and
 *   card colours when the pin is complete.
 *
 * `tileIds` narrows the tiles (default: all ten, hidden ones included — which
 * ones to OFFER is the caller's call, see `nearestPassingTile`).
 *
 * Throws when the brand cannot be parsed: that is the caller's input. A pin
 * value that cannot be parsed is stored data, so it only leaves the platform
 * option out — one bad row must not take the editor down.
 */
export function gradeBrandOnTiles(
  primaryColor: string,
  options: {
    tenantPin?: Record<string, string> | null;
    tileIds?: readonly SurfaceTileId[];
  } = {},
) {
  const { tenantPin, tileIds = SURFACE_TILE_IDS } = options;
  // Fail on an unreadable brand up front, so it can never pass for an
  // unreadable pin below (whose grade is skipped, not thrown).
  parseSourceColorChannels(primaryColor);

  const grades: Partial<Record<BackgroundOptionId, BrandGrade>> = {};
  for (const id of tileIds) {
    const { defaultColor, paperColor } = resolvedBackground(null, {
      [PRIMARY_MAIN]: primaryColor,
      [THEME_SURFACE_TILE_KEY]: id,
    });
    grades[id] = gradeBrandOnBackground(primaryColor, defaultColor, paperColor);
  }

  if (isDefined(tenantPin)) {
    const { defaultColor, paperColor } = resolvedBackground(tenantPin, {
      [PRIMARY_MAIN]: primaryColor,
    });
    try {
      grades.platform = gradeBrandOnBackground(
        primaryColor,
        defaultColor,
        paperColor,
      );
    } catch {
      // Unreadable pin — no platform grade, see above.
    }
  }
  return grades;
}

/** Lowercase `#rrggbb` for any engine colour; throws when unparseable. */
function toHex(color: string) {
  return `#${parseSourceColorChannels(color)
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`;
}

function oklabOf(color: string) {
  const { L, C, H } = hexToOklch(toHex(color));
  const hue = (H * Math.PI) / 180;
  return [L, C * Math.cos(hue), C * Math.sin(hue)];
}

/**
 * The option to suggest on an excluded one (`from`): among `candidates`, the
 * `recommended` option whose page colour is nearest `from`'s — Euclidean
 * distance in OKLab, so "nearest" is what a person would call similar — or,
 * when none is recommended, the nearest `not-recommended` one. `null` when
 * nothing passes; show `brandTooDarkOrLight` instead.
 *
 * `candidates` is the set the editor OFFERS: the enabled tiles plus the
 * platform option when it is available — never a hidden tile, whatever it
 * grades. `from` itself and any candidate without a grade are skipped.
 * Distances are measured between RESOLVED page colours (the `defaultColor`
 * of each grade), so `brand-dark` and the platform option count where they
 * actually render.
 *
 * Ties go to catalog order, the platform option last, whatever order
 * `candidates` comes in. If `from` has no grade there is nothing to measure
 * from, so catalog order alone decides.
 */
export function nearestPassingTile(
  grades: Partial<Record<BackgroundOptionId, BrandGrade>>,
  from: BackgroundOptionId,
  candidates: readonly BackgroundOptionId[],
) {
  const origin = grades[from];
  const originLab = isDefined(origin) ? oklabOf(origin.defaultColor) : null;
  const distanceTo = (color: string) => {
    if (originLab === null) {
      return 0;
    }
    const [L, a, b] = oklabOf(color);
    return Math.hypot(L - originLab[0], a - originLab[1], b - originLab[2]);
  };

  for (const state of ['recommended', 'not-recommended'] as const) {
    let nearest: { id: SuggestableOptionId; distance: number } | null = null;
    for (const id of BACKGROUND_OPTION_ORDER) {
      const grade = grades[id];
      if (
        id === from ||
        !candidates.includes(id) ||
        !isDefined(grade) ||
        grade.state !== state
      ) {
        continue;
      }
      const distance = distanceTo(grade.defaultColor);
      if (nearest === null || distance < nearest.distance) {
        nearest = { id, distance };
      }
    }
    if (nearest !== null) {
      return nearest.id;
    }
  }
  return null;
}

function luminanceOf(color: string) {
  const [r, g, b] = parseSourceColorChannels(color);
  return relativeLuminance(r, g, b);
}

/**
 * Which way to send the owner when NO offered background passes (spec §5.2):
 * `'too-dark'` ("choose a lighter brand colour") when the brand's relative
 * luminance is below the mean of `availableBackgrounds` (the page colours of
 * the offered options), otherwise `'too-light'`.
 *
 * With nothing to compare with, the brand's own end of the ladder decides
 * (`surfaceMode`). Throws on a colour it cannot parse.
 */
export function brandTooDarkOrLight(
  primaryColor: string,
  availableBackgrounds: readonly string[],
) {
  const brand = luminanceOf(primaryColor);
  if (availableBackgrounds.length === 0) {
    return surfaceMode(primaryColor) === 'light' ? 'too-light' : 'too-dark';
  }
  const mean =
    availableBackgrounds.reduce(
      (sum, background) => sum + luminanceOf(background),
      0,
    ) / availableBackgrounds.length;
  return brand < mean ? 'too-dark' : 'too-light';
}
